import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { adminLogin, getAdminToken } from "@/lib/adminApi";
import { LOGO_URL } from "@/lib/content";

export default function AdminLogin() {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // If already logged in, skip login and go straight to the pipeline
  if (getAdminToken()) {
    return <Navigate to="/admin" replace />;
  }

  const from = location.state?.from?.pathname || "/admin";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting || !password) return;
    setSubmitting(true);
    try {
      await adminLogin(password);
      toast.success("Welcome back.");
      navigate(from, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Incorrect password.";
      toast.error(typeof detail === "string" ? detail : "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-black text-[#F5F5F0] grain flex items-center justify-center px-6"
    >
      <div className="relative z-[2] w-full max-w-md">
        <div className="flex justify-center mb-10">
          <img
            src={LOGO_URL}
            alt="Swingle For Venues"
            className="h-24 md:h-28 w-auto object-contain"
          />
        </div>

        <div className="brackets bg-[#0A0A0A] border border-white/10 p-10">
          <div className="flex items-center justify-center gap-3 text-xs uppercase tracking-[0.3em] text-[#D9A6F5] mb-4">
            <Lock size={14} /> Admin Access
          </div>
          <h1 className="text-center text-3xl md:text-4xl font-black tracking-tighter mb-8">
            Pipeline Login
          </h1>

          <form onSubmit={onSubmit} className="space-y-6" data-testid="admin-login-form" noValidate>
            <div className="space-y-2">
              <label
                htmlFor="password"
                className="text-xs uppercase tracking-[0.22em] text-[#A3A3A3] font-medium"
              >
                Password
              </label>
              <Input
                id="password"
                type="password"
                autoFocus
                autoComplete="current-password"
                data-testid="admin-login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                required
                className="bg-black border-white/15 rounded-none h-12 text-[#F5F5F0] placeholder:text-[#A3A3A3]/60 focus-visible:ring-[#D9A6F5] focus-visible:ring-offset-0"
              />
            </div>

            <button
              type="submit"
              disabled={submitting || !password}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-3 bg-[#D9A6F5] text-black px-8 py-4 text-sm uppercase tracking-[0.22em] font-bold hover:bg-[#E8C8FF] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Signing in…
                </>
              ) : (
                "Enter Pipeline"
              )}
            </button>
          </form>
        </div>

        <p className="mt-8 text-center text-xs text-[#A3A3A3] uppercase tracking-[0.22em]">
          Internal use only
        </p>
      </div>
    </div>
  );
}
